/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/destructuring-assignment */
import { DSDataTableT } from '@elliemae/ds-data-table';
import Grid from '@elliemae/ds-grid';
import {
  useLoanDataFieldSelectorHook,
  useLookupOptions,
  useOriginalLoanInformationSelector,
} from '@frontend/pricing-search';
import { objectOf, shape, string } from 'prop-types';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setSelectedMIAmount } from '../../../../data/lockSummary/actions';
import {
  GetMIButton,
  getMIKey,
  isMIFirstPhaseLoading,
  isMILoading,
  isMIError,
  MI_ALREADY_ORDERED_TOOLTIP,
  MI_NOT_APPLICABLE_TOOLTIP,
  MILoadingSkeleton,
  useMIContext,
  useMIStore,
} from '../GetMIButton';
import Rights from '../../../../common/services/Rights';
import { handleSelectProductDetails } from '../../../../common/services/sessionHelpers';
import { KPI, kpiStart } from '../../../../common/services/kpiTracking';
import { getSelectedProductSummaryPath } from '../../../../route/getSummaryPath';
import {
  dispApr,
  dispCurrencyMask,
  dispDti,
  dispPrice,
  dispRate,
  getMICertification,
  NON_CONVENTIONAL_PRODUCT_TYPES,
  shouldDisplayAPR,
  shouldShowGetMIColumn,
  toNumber,
} from '../../helpers';
import {
  StyledAprCell,
  StyledBestExProgramLinks,
  StyledBestExRowSelected,
} from './styled';

export const DropdownDetails = (props: DSDataTableT.DetailsProps) => {
  const navigate = useNavigate();
  const cols = ['52%', '48%'];
  const program = props.row.original;
  const { rateDetails, setSlide, handleSelectRow } = program;
  const loanData = useLoanDataFieldSelectorHook() as any;
  const originalLoanInformation = useOriginalLoanInformationSelector();
  const { loanId } = useSelector(({ EPPS }) => EPPS.loanPrograms || {});
  const { contacts } = useSelector(({ EPPS }) => EPPS.loanInfo || {});
  const proposedMIAmount = useSelector(
    ({ EPPS }: any) =>
      EPPS.originalLoanInformation?.proposedMortgageInsuranceAmount,
  );
  const miCertification = getMICertification(contacts);
  const clientSettings = useLookupOptions('clientSettings');
  const loanTermsLookup = (useLookupOptions('loanTerms') as any[]) || [];

  const displayAPR = shouldDisplayAPR(clientSettings);
  const showGetMIColumn = useMemo(
    () =>
      shouldShowGetMIColumn({
        clientSettings,
        ltv: loanData?.loanInformation?.ltv
          ? parseFloat(loanData?.loanInformation?.ltv)
          : originalLoanInformation?.ltv,
        standardProducts: loanData?.standardProducts || [],
        loanTerms: loanData?.productType || [],
        loanTermsLookup,
        loanChannel: loanData?.loanInformation?.loanChannel,
      }),
    [
      clientSettings,
      originalLoanInformation?.ltv,
      loanData?.standardProducts,
      loanData?.productType,
      loanData?.loanInformation?.loanChannel,
      loanTermsLookup,
    ],
  );
  const isMIEligible = useMemo(
    () =>
      !NON_CONVENTIONAL_PRODUCT_TYPES.includes(
        program?.productTypeID as number,
      ),
    [program?.productTypeID],
  );
  const isGetMIDisabled = useMemo(
    () => !isMIEligible || !!miCertification,
    [isMIEligible, miCertification],
  );
  const disabledTooltip = useMemo(
    () =>
      miCertification && isMIEligible
        ? MI_ALREADY_ORDERED_TOOLTIP
        : MI_NOT_APPLICABLE_TOOLTIP,
    [isMIEligible, miCertification],
  );
  const dispatch = useDispatch();
  const { handleGetMI } = useMIContext();
  const store = useMIStore();
  const numCols = useMemo(
    () => (displayAPR ? 7 : 6) + (showGetMIColumn ? 2 : 0),
    [displayAPR, showGetMIColumn],
  );

  function handleSelectProduct(
    programID,
    rateDataID,
    lockEffectiveDate,
    wcpPolicyApplied,
  ) {
    const miAmount =
      store.getRowState(getMIKey(rateDataID, programID)).miAmount ?? null;
    dispatch(setSelectedMIAmount(miAmount));
    handleSelectProductDetails({
      loanId,
      programID,
      rateDataID,
      lockEffectiveDate,
      wcpPolicyApplied,
    });
    kpiStart(KPI.SelectRate);
    navigate(getSelectedProductSummaryPath());
  }
  const rightAlign = { width: '100%', textAlign: 'right', paddingRight: '3px' };
  const CellValue = ({ value }) => {
    return <span style={rightAlign}>{value}</span>;
  };
  CellValue.defaultProps = {
    value: '',
  };
  CellValue.propTypes = {
    value: string,
  };

  return (
    <>
      <Grid cols={cols}>
        <Grid />
        <Grid>
          <Grid cols={numCols} tabIndex={0}>
            {rateDetails.map(
              (
                {
                  programID,
                  rateDataID,
                  lockEffectiveDate,
                  wcpPolicyApplied = program.wcpPolicyApplied,
                  rate,
                  price,
                  priceVal,
                  pnI,
                  apr,
                  bRatio,
                  targetRow = program.target.rateDataID === rateDataID &&
                    program.target.programID === programID,
                },
                index,
              ) => {
                const {
                  status: miStatus,
                  miAmount: storedMIAmount,
                  aprValue,
                  miError,
                } = store.getRowState(getMIKey(rateDataID, programID));
                const miAmount =
                  isMIEligible && miCertification
                    ? proposedMIAmount
                    : storedMIAmount;
                const displayEstimatedMI =
                  clientSettings?.[0]?.options?.displayEstimatedMI;
                const displayPnI = toNumber(pnI) + (miAmount ?? 0);
                const displayApr = aprValue != null ? aprValue : apr;
                return (
                  <>
                    <Grid
                      height="32px"
                      style={{
                        paddingRight: 8,
                      }}
                    >
                      <StyledBestExRowSelected targetRow={targetRow} gridRow>
                        <Grid
                          width="100%"
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          <StyledBestExProgramLinks
                            onClick={() => {
                              handleSelectRow(rateDetails[index]);
                              handleSelectProduct(
                                programID,
                                rateDataID,
                                lockEffectiveDate,
                                wcpPolicyApplied,
                              );
                            }}
                          >
                            {dispRate(rate, priceVal)}
                          </StyledBestExProgramLinks>
                        </Grid>
                      </StyledBestExRowSelected>
                    </Grid>
                    <Grid
                      height="32px"
                      style={{
                        paddingRight: 8,
                        fontWeight: targetRow ? 'bold' : '',
                      }}
                    >
                      <StyledBestExRowSelected targetRow={targetRow} gridRow>
                        <CellValue value={dispPrice(price, priceVal)} />
                      </StyledBestExRowSelected>
                    </Grid>
                    <Grid
                      height="32px"
                      style={{
                        paddingRight: 8,
                        fontWeight: targetRow ? 'bold' : '',
                      }}
                    >
                      <StyledBestExRowSelected targetRow={targetRow} gridRow>
                        <CellValue value={dispCurrencyMask(priceVal)} />
                      </StyledBestExRowSelected>
                    </Grid>
                    <Grid
                      height="32px"
                      style={{
                        paddingRight: 8,
                        fontWeight: targetRow ? 'bold' : '',
                      }}
                    >
                      <StyledBestExRowSelected targetRow={targetRow} gridRow>
                        {isMIFirstPhaseLoading(miStatus) && !miCertification ? (
                          <MILoadingSkeleton />
                        ) : (
                          <CellValue value={dispCurrencyMask(displayPnI)} />
                        )}
                      </StyledBestExRowSelected>
                    </Grid>
                    {showGetMIColumn && (
                      <Grid style={{ paddingRight: 8 }}>
                        <StyledBestExRowSelected
                          targetRow={targetRow}
                          gridRow
                          showMIColumn
                          style={{ position: 'relative', zIndex: 3 }}
                        >
                          <Grid
                            width="100%"
                            justifyContent="flex-end"
                            alignItems="flex-end"
                          >
                            {isMIFirstPhaseLoading(miStatus) &&
                            !miCertification ? (
                              <MILoadingSkeleton />
                            ) : miAmount != null ? (
                              <CellValue value={dispCurrencyMask(miAmount)} />
                            ) : null}
                          </Grid>
                        </StyledBestExRowSelected>
                      </Grid>
                    )}
                    {showGetMIColumn && (
                      <Grid style={{ paddingRight: 8 }}>
                        <StyledBestExRowSelected
                          targetRow={targetRow}
                          gridRow
                          showMIColumn
                          style={{ position: 'relative', zIndex: 2 }}
                        >
                          <Grid
                            width="100%"
                            justifyContent="flex-end"
                            alignItems="flex-end"
                          >
                            {isMIFirstPhaseLoading(miStatus) &&
                            !miCertification ? (
                              <MILoadingSkeleton />
                            ) : (
                              <GetMIButton
                                isDisabled={isGetMIDisabled}
                                disabledTooltip={disabledTooltip}
                                isLoading={false}
                                isError={isMIError(miStatus)}
                                errorTooltip={miError?.tooltipMessage}
                                onClick={() =>
                                  handleGetMI({
                                    rateDataID,
                                    programID,
                                    displayEstimatedMI,
                                  })
                                }
                              />
                            )}
                          </Grid>
                        </StyledBestExRowSelected>
                      </Grid>
                    )}
                    {displayAPR && (
                      <Grid
                        height="32px"
                        style={{
                          paddingLeft: 8,
                          paddingRight: 0,
                          fontWeight: targetRow ? 'bold' : '',
                          overflow: 'hidden',
                        }}
                      >
                        <StyledBestExRowSelected
                          targetRow={targetRow}
                          gridRow
                          style={{ zIndex: 1 }}
                        >
                          {isMILoading(miStatus) ? (
                            <MILoadingSkeleton />
                          ) : (
                            <StyledAprCell>{dispApr(displayApr)}</StyledAprCell>
                          )}
                        </StyledBestExRowSelected>
                      </Grid>
                    )}
                    <Grid
                      height="32px"
                      pr="xxs"
                      style={{
                        fontWeight: targetRow ? 'bold' : 'normal',
                      }}
                    >
                      <StyledBestExRowSelected targetRow={targetRow} gridRow>
                        <CellValue value={dispDti(bRatio)} />
                      </StyledBestExRowSelected>
                    </Grid>
                    <Grid
                      height="32px"
                      style={{
                        paddingRight: 8,
                        fontWeight: targetRow ? 'bold' : '',
                      }}
                    >
                      <StyledBestExRowSelected targetRow={targetRow} gridRow>
                        <Grid
                          width="100%"
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          <StyledBestExProgramLinks
                            aria-label={`Details for rate ${dispRate(
                              rate,
                              priceVal,
                            )}`}
                            onClick={() => {
                              handleSelectRow(rateDetails[index]);
                              handleSelectProductDetails({
                                programID,
                                loanId,
                                rateDataID,
                                lockEffectiveDate,
                                wcpPolicyApplied,
                              });
                              setSlide({
                                rowData: rateDetails[index],
                                rateData: rateDataID,
                                open: false,
                                data: null,
                              });
                            }}
                          >
                            details
                          </StyledBestExProgramLinks>
                        </Grid>
                      </StyledBestExRowSelected>
                    </Grid>
                  </>
                );
              },
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

DropdownDetails.defaultProps = {
  row: {},
};

DropdownDetails.propTypes = {
  row: objectOf(shape({})),
};
